// Third-party
import React from 'react';

// Utils
import { formatMessage, FormattedMessage, formatLocalizedString } from '../../../../utils/i18n';
import colorUtils from '../../../../utils/colors';
import { getCorrelationLabelDisplayedText } from '../../../../utils/correlation-matrix';

// Components
import { Tooltip } from '../../tooltips';
import ComparisonHolding from '@/types/comparison-holding';
import CorrelationData from '@/types/correlation-data';

const DARK_TEXT_COLOR = '#5a6375';
const LIGHT_TEXT_COLOR = '#ffffff';

type Props = {
  rowHolding: ComparisonHolding;
  holdings: ComparisonHolding[];
  data: CorrelationData[];
  colorScale: chroma.Scale;
};

const CorrelationChartRow = ({ rowHolding, holdings, data, colorScale }: Props) => {
  const columns = data.map((datum, i) => {
    if (datum.value != null) {
      const { value } = datum;

      // Force values into a [0,1] range to cover full scale.
      const backgroundColor = colorScale((value + 1) * 0.5).toString();
      const color = colorUtils.isLight(backgroundColor, 0.6) ? DARK_TEXT_COLOR : LIGHT_TEXT_COLOR;

      // Find holding symbols for data point
      const symbols = holdings.filter(h => datum.holdings.indexOf(h.id) > -1).map(holding => holding.symbol || formatLocalizedString(holding.name));

      // Remove the integer part (or the first zero if value is 1 or -1): 0.12 -> .12 / 1.00 -> 1.0
      // IMPORTANT: Only use this function for values between -1 and 1, inclusively
      const shorten = (val: number) => val.toFixed(2).replace('0', '');

      const tooltipMessage = formatMessage('Charts.Correlation.ValueTooltip', {
        value: shorten(value),
        item1: symbols[0] || '',
        item2: symbols[1] || '',
      });

      return (
        <td className="value" key={i} style={{ backgroundColor, color }}>
          <Tooltip message={tooltipMessage}>
            <span>{shorten(value)}</span>
          </Tooltip>
        </td>
      );
    }

    return (
      <td key={i} className="value not-applicable">
        <FormattedMessage id="Common.NotApplicable" />
      </td>
    );
  });

  return (
    <tr>
      <td className="correlation-y-label">
        <Tooltip message={rowHolding.name ? formatLocalizedString(rowHolding.name) : undefined}>
          <span className="correlation-label">{getCorrelationLabelDisplayedText(rowHolding)}</span>
        </Tooltip>
      </td>

      {columns}
    </tr>
  );
};

export default CorrelationChartRow;
